import { Link } from "react-router";
import { Wallet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { formatCurrencyEUR, formatDate } from "@/lib/utils";
import { MDF_FUND_STATUS_LABEL, type MdfFundStatus } from "@colab/shared";
import { useFunds } from "./use-funds";
import { CreateFundDialog } from "./create-fund-dialog";

// Phase 7 sub-bucket 15 — admin funds index. Ports
// /app/admin/mdf/page.tsx (130 LOC). The server worker aggregates
// allocated / available / open_request_count per fund so the cards render
// from a single GET (no per-fund round-trip dance like the legacy Promise.all
// over loadFundDetail).
//
// Phase 6 mutation hooks (useCreateFund / useCloseFund / useUpdateFund)
// invalidate `queryKeys.mdf.funds()` so the index refreshes on any write.

const FUND_STATUS_VARIANT: Record<
  MdfFundStatus,
  "default" | "outline" | "secondary"
> = {
  open: "default",
  paused: "outline",
  closed: "secondary",
};

export function MdfFundsListPage() {
  const query = useFunds();
  const funds = query.data ?? [];
  const canWrite = true; // gated upstream by ProtectedRoute + admin layout

  return (
    <div>
      <header className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Admin
          </p>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">
            Marketing Funds
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Vendor-owned co-marketing budgets allocated per partner. Open a
            fund to manage allocations, review requests, and track ROI.
          </p>
        </div>
        {canWrite && <CreateFundDialog />}
      </header>

      {query.isLoading ? (
        <FundCardsSkeleton />
      ) : query.isError ? (
        <EmptyState
          icon={Wallet}
          title="Couldn't load funds"
          description={query.error?.message ?? "Try refreshing."}
        />
      ) : funds.length === 0 ? (
        <EmptyState
          icon={Wallet}
          title="No funds yet"
          description="Create your first fund to allocate marketing budget to partners."
          action={canWrite ? <CreateFundDialog /> : undefined}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {funds.map((fund) => {
            const pct =
              fund.total_amount_eur > 0
                ? Math.min(
                    Math.round(
                      (fund.allocated_amount_eur / fund.total_amount_eur) * 100,
                    ),
                    100,
                  )
                : 0;
            return (
              <Link
                key={fund.id}
                to={`/admin/mdf/${fund.id}`}
                className="group flex flex-col rounded-xl border bg-card p-5 transition hover:border-foreground/15"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="truncate font-display text-base font-semibold tracking-tight">
                      {fund.name}
                    </h3>
                    <p className="mt-0.5 truncate text-[11px] text-muted-foreground">
                      /{fund.slug}
                    </p>
                  </div>
                  <Badge variant={FUND_STATUS_VARIANT[fund.status]}>
                    {MDF_FUND_STATUS_LABEL[fund.status]}
                  </Badge>
                </div>

                <p className="mt-3 text-xs text-muted-foreground">
                  {formatDate(fund.period_start)} → {formatDate(fund.period_end)}
                </p>

                <div className="mt-4">
                  <div className="flex items-baseline justify-between">
                    <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                      Total
                    </span>
                    <span className="font-display text-xl font-semibold tabular-nums">
                      {formatCurrencyEUR(fund.total_amount_eur)}
                    </span>
                  </div>
                  <Progress value={pct} className="mt-3 h-1.5" />
                  <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground tabular-nums">
                    <span>
                      Allocated{" "}
                      {formatCurrencyEUR(fund.allocated_amount_eur)}
                    </span>
                    <span>
                      Available{" "}
                      {formatCurrencyEUR(fund.available_amount_eur)}
                    </span>
                  </div>
                  {fund.open_request_count > 0 && (
                    <p className="mt-2 text-[11px] font-medium text-amber-600 dark:text-amber-400">
                      {fund.open_request_count} open request
                      {fund.open_request_count === 1 ? "" : "s"}
                    </p>
                  )}
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

function FundCardsSkeleton() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="rounded-xl border bg-card p-5">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="mt-2 h-3 w-1/3" />
          <Skeleton className="mt-6 h-7 w-1/2" />
          <Skeleton className="mt-3 h-1.5 w-full" />
        </div>
      ))}
    </div>
  );
}
